import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { PersonHelpers } from '@frontend/api-person';
import { AdaptoActions } from '@frontend/adapto-actions';
import { Actions } from '@frontend/contact-actions';
import { formatDate } from '@frontend/date';
import { FirstWordBoldText, WidgetizedDashboardTrackingIds } from '@frontend/grid-dashboard';
import { useTranslation } from '@frontend/i18n';
import { useLocationTimezone } from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import { Text, usePopoverMenu } from '@frontend/design-system';
import { OverlappingEventIndicator } from '../../components/calendar-view/calendar-view-v3/overlapping-event-indicator';
import { PRIMARY_EVENT_COLOR, EVENT_BACKGROUND_COLOR } from '../../constants';
import { useGetAppointmentDetails } from '../../hooks/use-get-appointment-details';
import { ExtendedAppointment } from '../../types';

interface Props {
  appointmentInfo: ExtendedAppointment;
}

const appointmentsWidgetTrackingId =
  WidgetizedDashboardTrackingIds.widgetSpecificTrackingId('appointments-widget-action');

export const AppointmentCard = ({ appointmentInfo }: Props) => {
  const { t } = useTranslation('schedule');
  const popoverMenuProps = usePopoverMenu();
  const { locationTimezoneAbbr } = useLocationTimezone(appointmentInfo?.locationId ?? '');

  const person = appointmentInfo.person ?? {};
  const personName = PersonHelpers.getFullName({
    FirstName: person.firstName,
    LastName: person.lastName,
    PreferredName: person.preferredName,
  });
  const fromTime = formatDate(appointmentInfo.start, 'h:mm A');
  const toTime = formatDate(dayjs(appointmentInfo.start).add(appointmentInfo.duration ?? 0, 'm'), 'h:mm A');
  const personPhoneNumber = person.mobilePhone || person.homePhone || person.workPhone || '';

  const primaryColor = appointmentInfo.displayOptions?.primaryColor ?? PRIMARY_EVENT_COLOR;

  const backgroundColor = appointmentInfo.displayOptions?.backgroundColor ?? EVENT_BACKGROUND_COLOR;

  const { openModal: handleOpenAppointmentModal } = useGetAppointmentDetails({
    appointmentId: appointmentInfo.id || '',
    locationId: appointmentInfo.locationId || '',
    appointments: [appointmentInfo],
  });

  const handleViewAppointmentClick = () => {
    popoverMenuProps.close();
    handleOpenAppointmentModal();
  };

  return (
    <li css={[containerStyle, { borderColor: primaryColor, backgroundColor }]}>
      <div>
        <FirstWordBoldText size='medium'>{personName}</FirstWordBoldText>
        <div css={{ display: 'flex', gap: theme.spacing(0.5), alignItems: 'center' }}>
          <Text size='small' color='light'>
            {fromTime} - {toTime} {locationTimezoneAbbr ?? ''}
          </Text>
          <OverlappingEventIndicator variant='full' visible={appointmentInfo.isOverlapping} />
        </div>
      </div>
      <AdaptoActions variant='icon-buttons' iconColor='light' spacing={0.5} size='small'>
        <Actions.Call
          label={t('Call {{name}}', {
            name: personName || personPhoneNumber,
          })}
          context={{
            personId: person.personId,
            phoneNumber: personPhoneNumber,
            locationId: appointmentInfo.locationId,
          }}
          trackingIdSuffix='appointments-widget'
        />

        <Actions.Message
          label={t('Message {{name}}', {
            name: personName || personPhoneNumber,
          })}
          context={{
            personId: person.personId,
            personPhone: personPhoneNumber,
            groupIds: appointmentInfo.locationIds,
          }}
          trackingIdSuffix='appointments-widget'
        />
        <AdaptoActions.Action
          label={t('View Appointment')}
          icon='calendar-small'
          onClick={handleViewAppointmentClick}
          trackingId={appointmentsWidgetTrackingId('view-appointment')}
        />
      </AdaptoActions>
    </li>
  );
};

const containerStyle = css({
  borderLeft: '4px solid',
  borderRadius: theme.borderRadius.small,
  padding: theme.spacing(0.5, 2),
  marginBottom: theme.spacing(1),
  boxShadow: theme.shadows.light,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});
